import ExcelJS from "exceljs";
import type { CompanyOrderUsecase } from "../../../../domain/usecases/company/company-order-usecase";
import { mapApiOrderToView } from "./utils";
import { orderDisplayTitle } from "./orderDisplayTitle";
import { ORDER_STATUS_LABELS } from "./constants";

export const orderDateBounds = (start: string, end: string) => ({
  dateFrom: start ? `${start}T00:00:00` : undefined,
  dateTo: end ? `${end}T23:59:59.999999` : undefined,
});

/** Fetch every matching page, never just the currently rendered rows. */
export async function createOrderWorkbook(
  usecase: CompanyOrderUsecase, companyId: number, status: string | undefined,
  keyword: string, dateFrom?: string, dateTo?: string,
) {
  if (dateFrom && dateTo && dateFrom > dateTo) throw new Error("시작일은 종료일보다 늦을 수 없습니다.");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("주문 목록");
  sheet.columns = [
    { header: "주문번호", key: "orderNumber", width: 24 },
    { header: "주문자", key: "customerName", width: 20 },
    { header: "상품명", key: "productName", width: 30 },
    { header: "작업물 제목 / 주문 구분", key: "workTitle", width: 36 },
    { header: "수량", key: "quantity", width: 12 },
    { header: "금액", key: "totalAmount", width: 18 },
    { header: "상태", key: "status", width: 18 },
    { header: "주문일", key: "orderDate", width: 16 },
    { header: "일자 기준", key: "dateBasis", width: 18 },
  ];
  const ids = new Set<string>();
  let expectedTotal: number | undefined;
  let totalPages = 1;
  for (let page = 0; page < totalPages; page++) {
    const result = await usecase.getOrders(companyId, status, keyword, page, 100, dateFrom, dateTo);
    if (expectedTotal === undefined) {
      expectedTotal = result.totalElements;
      totalPages = result.totalPages;
    } else if (expectedTotal !== result.totalElements || totalPages !== result.totalPages) {
      throw new Error("내보내는 동안 주문 목록이 변경되었습니다. 다시 다운로드해 주세요.");
    }
    for (const raw of result.items) {
      const order = mapApiOrderToView(raw);
      if (ids.has(order.id)) throw new Error("내보내는 동안 주문 순서가 변경되었습니다. 다시 다운로드해 주세요.");
      ids.add(order.id);
      // Explicit primitive strings are text cells, including values starting with =, +, - or @.
      sheet.addRow({ ...order, workTitle: orderDisplayTitle(order),
        status: ORDER_STATUS_LABELS[order.status] ?? order.status,
        dateBasis: order.isLegacyDate ? "기존 생성일" : "실제 접수일" });
    }
  }
  if (ids.size !== (expectedTotal ?? 0)) throw new Error("전체 주문을 가져오지 못했습니다. 다시 다운로드해 주세요.");
  sheet.getColumn("quantity").numFmt = "#,##0";
  sheet.getColumn("totalAmount").numFmt = "#,##0";
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = { from: "A1", to: "I1" };
  return workbook;
}

